import React from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { cn } from "@/lib/utils";
import { formatPercent } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, AlertTriangle, Lock } from "lucide-react";

// ---------- Money ----------
export function Money({ value, className, colorNegative = false, testId }) {
  const { money } = useCurrency();
  const neg = typeof value === "number" && value < 0;
  return (
    <span
      data-testid={testId}
      className={cn("num", colorNegative && neg && "text-[rgb(185,28,28)]", className)}
    >
      {money(value)}
    </span>
  );
}

// ---------- Status chips ----------
const STATUS = {
  ok: { cls: "bg-[rgba(21,128,61,0.10)] text-[rgb(21,128,61)] border-[rgba(21,128,61,0.22)]", label: "OK" },
  within: { cls: "bg-[rgba(21,128,61,0.10)] text-[rgb(21,128,61)] border-[rgba(21,128,61,0.22)]", label: "WITHIN BUDGET" },
  exceeded: { cls: "bg-[rgba(185,28,28,0.10)] text-[rgb(185,28,28)] border-[rgba(185,28,28,0.22)]", label: "EXCEEDED" },
  flagged: { cls: "bg-[rgba(180,83,9,0.12)] text-[rgb(180,83,9)] border-[rgba(180,83,9,0.22)]", label: "FLAGGED" },
  locked: { cls: "bg-[rgba(51,65,85,0.10)] text-[rgb(51,65,85)] border-[rgba(51,65,85,0.22)]", label: "LOCKED" },
};

export function StatusBadge({ status, label, testId, className }) {
  const s = STATUS[status] || STATUS.ok;
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        s.cls,
        className
      )}
    >
      {status === "exceeded" && <AlertTriangle className="h-3 w-3" />}
      {(status === "ok" || status === "within") && <CheckCircle2 className="h-3 w-3" />}
      {status === "flagged" && <AlertTriangle className="h-3 w-3" />}
      {status === "locked" && <Lock className="h-3 w-3" />}
      {label || s.label}
    </span>
  );
}

// ---------- KPI card ----------
export function KpiCard({ label, value, sub, accent = "teal", icon: Icon, progress, testId }) {
  const accents = {
    teal: "text-[rgb(15,118,110)]",
    blue: "text-[rgb(37,99,235)]",
    amber: "text-[rgb(180,83,9)]",
    green: "text-[rgb(21,128,61)]",
    slate: "text-[rgb(51,65,85)]",
    red: "text-[rgb(185,28,28)]",
  };
  return (
    <Card data-testid={testId} className="rounded-xl border bg-card p-4 shadow-[0_1px_0_rgba(16,24,40,0.04)] transition-shadow duration-200 hover:shadow-md">
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {Icon && <Icon className={cn("h-4 w-4", accents[accent])} />}
      </div>
      <p className={cn("mt-2 text-2xl md:text-[1.75rem] font-semibold tracking-tight tabular-nums font-heading", accents[accent])}>
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      {typeof progress === "number" && (
        <div className="mt-3 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full rounded-full bg-[rgb(15,118,110)]"
            style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
          />
        </div>
      )}
    </Card>
  );
}

// ---------- Section card ----------
export function SectionCard({ title, description, action, children, className, testId }) {
  return (
    <Card data-testid={testId} className={cn("rounded-xl border bg-card", className)}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div>
            {title && <h3 className="text-sm font-semibold tracking-wide text-foreground font-heading">{title}</h3>}
            {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

// ---------- Page header ----------
export function PageHeader({ title, description, children }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight font-heading">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

// ---------- Data health item ----------
export function HealthItem({ label, ok, detail, testId }) {
  return (
    <div data-testid={testId} className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-sm font-medium",
          ok ? "text-[rgb(21,128,61)]" : "text-[rgb(185,28,28)]"
        )}
      >
        {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {detail}
      </span>
    </div>
  );
}

// ---------- Loading / Empty ----------
export function Loading({ rows = 4 }) {
  return (
    <div data-testid="loading-skeleton" className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  );
}

export function EmptyState({ title, message, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-card px-6 py-14 text-center">
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
